import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import PageSeo from "@/components/PageSeo";
import { PILLAR_ARTICLES } from "@/data/pillar-content";

/**
 * Reusable long-form pillar-article page.
 *
 * Route:      /:slug   (mounted for each key in PILLAR_ARTICLES)
 * Data:       /src/data/pillar-content.js
 * Purpose:    Topical-authority content targeting bottom-of-funnel + AI-search queries.
 * Structure:  Hero (H1) → TL;DR box → Sections (H2/H3) → FAQ → Related → CTA.
 *
 * Every article emits per-page BreadcrumbList + Article + FAQPage schema.
 */
export default function PillarArticle({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const article = PILLAR_ARTICLES[slug];

  const jsonLd = useMemo(() => {
    if (!article) return null;
    const url = `https://relatable.studio/${slug}`;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: article.metaTitle.split(" · ")[0],
          description: article.metaDescription,
          author: {
            "@type": "Person",
            "@id": "https://relatable.studio/about#rudhra-vishnu",
            name: "Rudhra Vishnu",
          },
          publisher: { "@id": "https://relatable.studio/#organization" },
          datePublished: "2026-02-01",
          dateModified: "2026-02-01",
          mainEntityOfPage: url,
          inLanguage: "en-IN",
          articleSection: article.category || "Resources",
        },
        {
          "@type": "FAQPage",
          mainEntity: (article.faqs || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
  }, [article, slug]);

  if (!article) return <Navigate to="/" replace />;

  return (
    <main
      data-testid={`pillar-article-${slug}`}
      className="bg-ink text-bone min-h-screen"
    >
      <PageSeo
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/${slug}`}
        type="article"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Resources", path: `/${slug}` },
          { name: article.metaTitle.split(" · ")[0], path: `/${slug}` },
        ]}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-14 md:pb-20 px-6 md:px-12 lg:px-24">
        <p className="eyebrow text-fog mb-6">◇ {article.eyebrow}</p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.94] max-w-6xl"
        >
          {article.h1a}
          <br />
          <span className="text-relatable">{article.h1b}</span>
        </motion.h1>
        {article.dek && (
          <p className="mt-10 max-w-3xl text-fog text-lg md:text-xl leading-relaxed">
            {article.dek}
          </p>
        )}
      </section>

      {/* TL;DR summary — the block AI engines will lift verbatim as an answer */}
      {article.tldr && article.tldr.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-14 md:pb-20">
          <div className="border-t border-b border-line py-8 md:py-10 max-w-4xl">
            <p className="eyebrow text-relatable mb-5">◇ TL;DR</p>
            <ul className="space-y-4">
              {article.tldr.map((t, i) => (
                <li key={i} className="text-bone text-base md:text-lg leading-relaxed flex gap-3">
                  <span className="font-mono text-fog text-xs pt-1.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Body sections */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
        <div className="max-w-4xl space-y-14 md:space-y-20">
          {article.sections.map((s, i) => (
            <div key={i} data-testid={`pillar-section-${i}`}>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-tight text-bone">
                {s.h2}
              </h2>
              {s.body && (
                <div className="mt-6 space-y-5 text-fog text-base md:text-lg leading-relaxed">
                  {s.body.map((p, pi) => (
                    <p key={pi}>{p}</p>
                  ))}
                </div>
              )}
              {s.subsections && (
                <div className="mt-8 space-y-8">
                  {s.subsections.map((sub, si) => (
                    <div key={si} className="pl-4 border-l border-line">
                      <h3 className="font-display text-xl md:text-2xl font-medium text-bone leading-tight">
                        {sub.h3}
                      </h3>
                      <p className="mt-3 text-fog text-base md:text-lg leading-relaxed">
                        {sub.body}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {article.faqs && article.faqs.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24 border-t border-line pt-16 md:pt-20">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-4 md:sticky md:top-28 self-start">
              <p className="eyebrow text-fog mb-4">◇ FAQ</p>
              <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[0.95] text-bone">
                Common
                <br />
                <span className="text-relatable">questions.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8 space-y-8">
              {article.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group border-b border-line pb-6"
                  data-testid={`pillar-faq-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                    <h3 className="font-display text-xl md:text-2xl text-bone leading-tight">
                      {f.q}
                    </h3>
                    <span className="eyebrow text-fog shrink-0 mt-1 group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-fog text-base md:text-lg leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24 border-t border-line pt-16 md:pt-20">
          <p className="eyebrow text-fog mb-8">◇ Related</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {article.related.map((r, i) => (
              <Link
                key={i}
                to={r.href}
                data-testid={`pillar-related-${i}`}
                className="group flex items-center justify-between gap-6 p-6 border border-line hover:border-bone transition-colors"
              >
                <span className="font-display text-lg md:text-xl text-bone">
                  {r.name}
                </span>
                <ArrowUpRight className="w-5 h-5 shrink-0 text-fog group-hover:text-relatable transition-colors" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32 border-t border-line pt-16 md:pt-20">
        <div className="max-w-4xl">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.95] text-bone">
            Start with the <span className="text-relatable">problem.</span>
          </h2>
          <p className="mt-6 text-fog text-lg leading-relaxed max-w-2xl">
            No deck required. We reply within 24 hours, often within 4.
          </p>
          <Link
            to="/contact"
            data-testid="pillar-cta-contact"
            className="mt-10 inline-flex items-center gap-3 px-8 py-5 bg-bone text-ink eyebrow text-sm hover:bg-relatable hover:text-bone transition-colors"
          >
            Send a brief
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
